import React, { useRef } from 'react';
import useFocusOnInit from '../../../hooks/useFocusOnInit';
import PropTypes from 'prop-types';
import { classnames } from '../../../helpers/classnames';
import arePropsEqual from '../../../helpers/arePropsEqual';
import { bem } from '../../../helpers/styles';
import SVG from '../SVG/SVG';
import styles from './Input.css';
import { float, numberRegex } from '../../../helpers/parse';
import { omit } from '../../../helpers/omit';

const Input = React.memo(({
  type = 'text',
  mode,
  placeholder,
  name,
  id,
  value,
  onChange = () => null,
  onBlur = () => null,
  onFocus = () => null,
  className,
  style,
  error,
  block,
  readonly,
  iconRight,
  labelRight,
  inputRef = null,
  isFocusOnInit = false,
  ...props
}) => {
  const ref = useRef();

  const classname = classnames([
    bem(styles, 'Input', { error, block, icon: !!iconRight || !!labelRight }),
    className,
  ]);

  const handleChange = (value) => {
    let newVal = value;

    if (mode === 'number') newVal = numberRegex(value);
    if (type == 'number') newVal = float(value);

    return newVal;
  };

  useFocusOnInit(inputRef ?? ref, isFocusOnInit, onFocus);

  const input = (
    <input
      ref={inputRef ?? ref}
      type={type}
      placeholder={placeholder}
      name={name}
      id={id}
      value={value ?? ''}
      onChange={({ target: { value } }) => {
        const parseValue = handleChange(value);
        onChange(parseValue);
      }}
      onBlur={(event) => {
        if (mode === 'number') {
          onBlur();
        } else {
          onBlur(type === 'number' ? parseFloat(event.target.value) : event.target.value);
        }
      }}
      onKeyPress={(event) => {
        if (mode === 'number') {
          const { key } = event;
          const { value, selectionStart } = event.target;
          const nextValue = `${value.slice(0, selectionStart)}${key}${value.slice(selectionStart)}`;
          const parseValue = handleChange(nextValue);

          if (nextValue !== parseValue) {
            onChange(parseValue);
            event.preventDefault();
          }
        }
      }}
      onFocus={onFocus}
      className={classname}
      style={style}
      readOnly={readonly}
      {...omit(props, 'label', 'initialValue')}
      size={1}
    />
  );

  return iconRight || labelRight ? (
    <div className={bem(styles, 'Input__iconWrapper', { error })} style={style}>
      {input}
      {labelRight && <div className={styles.Input__labelRight}>{labelRight}</div>}
      {iconRight && <SVG className={styles.Input__iconRight} glyph={iconRight} />}
    </div>
  ) : (
    input
  );
}, (oldProps, newProps) => {
  return arePropsEqual(oldProps, newProps);
});

Input.displayName = 'Input';

Input.propTypes = {
  inputRef: PropTypes.object,
  isFocusOnInit: PropTypes.bool,
  mode: PropTypes.oneOf(['text', 'number', 'time', 'date']),
  type: PropTypes.oneOf(['text', 'number', 'time', 'date']),
  placeholder: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  error: PropTypes.bool,
  block: PropTypes.bool,
  readonly: PropTypes.bool,
  iconRight: PropTypes.string,
  labelRight: PropTypes.string,
  initialValue: PropTypes.string,
};

export default Input;
